'use client';
import { useState, useMemo } from 'react';
import { FaCalendarCheck } from 'react-icons/fa';

import BookingButton from '@/components/BookingButton';
import ButtonContainer from '@/components/ButtonContainer';
import CloudImage from '@/components/CloudImage';
import CustomGrid from '@/components/CustomGrid';
import Icon from '@/components/Icon';
import MediaContainer from '@/components/MediaContainer';
import SectionWrapper from '@/components/SectionWrapper';
import VehicleClassifierPopup from '@/components/vehicle-classifier/VehicleClassifierPopup';

import { services } from '../servicesPageContent';

export default function CarDetailingServices() {
  const [activeCategory, setActiveCategory] = useState('Full Detail');

  // Pre-filter to only car-detailing services
  const carDetailing = useMemo(
    () => services.filter((s) => s.category === 'car-detailing'),
    [],
  );

  // Define custom ordering per category
  const orderMapping = useMemo(
    () => ({
      'Full Detail': {
        'mad-elite-full-detail': 1,
        'mad-deluxe-full-detail': 2,
        'mad-essentials-full-detail': 3,
      },
      'Interior Only': {
        'full-interior-detail': 1,
        'total-interior-detail': 2,
      },
      'Exterior Only': {
        'exterior-wash-protect': 1,
        'exterior-decon-protect': 2,
        'exterior-clay-polish': 3,
      },
    }),
    [],
  );

  // Filter + sort whenever the category changes
  const filteredServices = useMemo(() => {
    const filtered = carDetailing.filter(
      (svc) => svc.serviceType === activeCategory,
    );
    return filtered.sort((a, b) => {
      const orderA = orderMapping[activeCategory][a.slug] ?? 0;
      const orderB = orderMapping[activeCategory][b.slug] ?? 0;
      return orderA - orderB;
    });
  }, [activeCategory, carDetailing, orderMapping]);

  return (
    <SectionWrapper id="detailing-packages">
      {/* Category Tabs */}
      <div className="mb-6 flex justify-center space-x-4">
        {['Full Detail', 'Interior Only', 'Exterior Only'].map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={`rounded px-6 py-2 text-sm font-semibold transition-all duration-200 ${
              activeCategory === cat
                ? 'bg-primary text-white shadow-lg'
                : 'bg-gray-300 hover:bg-gray-400'
            } `}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Services List */}
      {filteredServices.length > 0 ? (
        <div className="space-y-8">
          {filteredServices.map((service) => (
            <div
              key={service.slug}
              id={service.slug}
              className="bg-secondary text-secondary-foreground tablet:p-8 space-y-8 rounded-sm p-4"
            >
              <div className="laptop:flex-row flex flex-col gap-8">
                <MediaContainer className="laptop:aspect-square aspect-video flex-1">
                  <CloudImage
                    publicId={service.imageSrc}
                    alt={service.title}
                    fill
                  />
                </MediaContainer>

                <div className="flex-1 space-y-2">
                  <h3 className="uppercase">{service.title}</h3>
                  <p className="text-sm">{service.description}</p>
                  <dl className="grid grid-cols-[max-content_1fr] gap-x-2 gap-y-2">
                    <dt className="text-sm font-semibold">Best For:</dt>
                    <dd>{service.bestFor}</dd>
                    <dt className="text-sm font-semibold">Rotation:</dt>
                    <dd>{service.recommendedTimeframe}</dd>
                  </dl>

                  <CustomGrid
                    variant={
                      service.exteriorServiceList.length > 0 &&
                      service.interiorServiceList.length > 0
                        ? 'two'
                        : 'single'
                    }
                  >
                    {service.exteriorServiceList.length > 0 && (
                      <div>
                        <h4 className="mb-2">Exterior</h4>
                        <ul className="ml-5 list-disc space-y-1 text-sm">
                          {service.exteriorServiceList.map((item, i) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {service.interiorServiceList.length > 0 && (
                      <div>
                        <h4 className="mb-2 font-semibold">Interior</h4>
                        <ul className="ml-5 list-disc space-y-1 text-sm">
                          {service.interiorServiceList.map((item, i) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CustomGrid>
                </div>
              </div>

              {/* Pricing */}
              <CustomGrid variant="quad">
                {service.servicePricing.map((price, i) => (
                  <div
                    key={i}
                    className="space-y-1 rounded-sm border-2 p-4 text-center font-semibold capitalize"
                  >
                    <p className="text-xs">
                      {['small', 'medium', 'large', 'extra large'][i]} vehicle
                    </p>
                    <p className="text-xs">starting at</p>
                    <p className="text-primary text-xl">${price}</p>
                  </div>
                ))}
              </CustomGrid>

              {/* CTAs */}
              <ButtonContainer>
                <BookingButton>
                  <Icon icon={FaCalendarCheck} className="mr-2" />
                  Book Now
                </BookingButton>
                <VehicleClassifierPopup
                  buttonTitle="Get My Vehicle Size"
                  buttonVariant="ghost"
                />
              </ButtonContainer>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-center text-gray-400">
          No services available in this category.
        </p>
      )}
    </SectionWrapper>
  );
}
